<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->


<template>
  <div class="wy-interactions-dashboard-table-detail-genie-bot" v-loading="loading">
    <div v-if="hasError" class="genie-error">
      Your Genie Bot session could not be completed. Please try again.
    </div>
    <iframe 
      v-else-if="iframeUrl"
      id="genie-iframe"
      width="100%" 
      height="100%" 
      style="border: 0;" 
      :src="iframeUrl"
      allow="clipboard-read; clipboard-write"
    ></iframe>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import WyGenie from '../../../library/wy-genie.js';

export default Vue.extend({
  name: 'wy-interactions-dashboard-table-detail-genie-bot',
  props: ['parent', 'activeTab'],

  data() {
    return {
      loading: false,
      initDate: null,
      iframeUrl: '',
      sessionId: null,
      hasError: false,
    }
  },

  computed: {
    ...mapGetters(['dataModel', 'isRoleAllow', 'processList', 'authUser', 'tokenInfo']),

  },

  methods: {
    ...mapActions(['fetchGenieSession', 'postGenieLogging']),

    async startLoading() {
      this.initDate = new Date();
      if (!this.loading) {
        this.logEventLoaded();
      }
      
      await this.loadGenieSession();
    },

    logEventLoaded() {
      if (this.initDate) {
        this.initDate = null;
      }
    },

    async loadGenieSession() {
      this.loading = true;

      const genieSessionResponse = await this.fetchGenieSession({
        topicDisplayName: this.parent.currentItem.topicDisplayName, 
        topics: this.parent.currentItem.mergedTopics, 
        isOthers: this.parent.currentItem.isOthers
      });

      if (genieSessionResponse) {
        this.sessionId = genieSessionResponse.sessionId;
        const veidHint = genieSessionResponse.veid;
        const baseUrl = genieSessionResponse.baseUrl;

        if(baseUrl && veidHint && this.sessionId) {
          const params = new URLSearchParams({
              sessionId: this.sessionId,
              authConfig: 'idv2',
              language: 'en_US',
              login_hint: this.authUser.user.email,
              v_veid_hint: veidHint,
              isHideRightPane: true
          });
          
          this.iframeUrl = `${baseUrl}/app/index.html?${params.toString()}`;
          this.hasError = false;
        }
        else {
          this.hasError = true;
        }
      } else {
        this.hasError = true;
      }

      this.loading = false;
    },

    handleOpenMediaPlayer(event) {
      const conversationId = event.data.detail.interaction ? event.data.detail.interaction.id : null;
      if(this.tokenInfo && this.tokenInfo.authHost && conversationId) {
        const mediaLink = `https://${this.tokenInfo.authHost}/#/recordings/${conversationId}/ccr/player`
        window.open(mediaLink);
      }
    },

    handleGenieLogging(event) {
      const chatHistory = event.data.detail.chatHistory;
      const lastMessage = Array.isArray(chatHistory) ? chatHistory[chatHistory.length - 1] : undefined;

      if(!lastMessage) {
        return;
      }

      const payload = WyGenie.createGenieLoggingPayload("trendingTopics", lastMessage, this.parent.currentItem.totalVolume, {
        topicDisplayName: this.parent.currentItem.topicDisplayName,
      })

      // load genieTraceId from session storage
      const genieTraceId = sessionStorage.getItem('genieTraceId')

      if(payload !== null) {
        // prevent submitting the same message multiple times, only submit when trace_id is different or role is user
        if(lastMessage && ((lastMessage.content && lastMessage.content.trace_id && lastMessage.content.trace_id !== genieTraceId && lastMessage.role === 'assistant') || lastMessage.role === 'user')) {
          // store lastMessage.content.trace_id in session storage
          // only for 'assistant' role, since it will resend the same message when reloaded
          if(lastMessage.content && lastMessage.content.trace_id && lastMessage.role === 'assistant') {
            sessionStorage.setItem('genieTraceId', lastMessage.content.trace_id);
          }
          
          if(payload && typeof payload === 'object' && Object.keys(payload).length > 0) {
            this.postGenieLogging(payload);
          } else {
            console.warn('Invalid payload received');
          }
        }
      } else {
        console.warn('Invalid payload created:');
      }
    },

    genieBotMessageEvent(event) {
      if(event && event.origin && event.origin.includes("hydra.verint.com") && event.data && event.data.key == "GenieBot_interactionQuoteClicked") {
        this.handleOpenMediaPlayer(event);
      }
      else if(event && event.origin && event.origin.includes("hydra.verint.com") && event.data && event.data.key == "GenieBot_chatHistoryChanged") {
        this.handleGenieLogging(event);
      }
    },
  },

  created() {
    window.addEventListener('message', this.genieBotMessageEvent);
  },
  
  beforeDestroy() {
    // Clean up event listeners
    window.removeEventListener('message', this.genieBotMessageEvent);
  }
});
</script>

<style lang="scss">
  .wy-interactions-dashboard-table-detail-genie-bot {
    width: calc(100% - 25px);
    height: calc(100vh - 330px);
    min-width: 1000px;
    padding: 10px;
    border: 2px #5DB3C6 solid;
    border-radius: 4px;

    .genie-error {
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }
  }
</style>